/**
 * Opens the store's chat from a button inside the page.
 *
 * The chat is `<shopify-chat>`: the Inbox app embed registers the element and
 * snippets/chat-drawer.liquid hosts it, so the pill in the corner and this
 * button are two launchers for one thing. `show()` is the entry point the pill
 * uses, and chat-drawer.liquid already listens for the chat's own mode events
 * to dock it into the theme drawer -- so nothing here touches `mode`, the
 * drawer, or the `open` attribute directly.
 *
 * Buttons render `hidden` and are revealed only once the element is registered.
 * A shop without Inbox -- app uninstalled, embed disabled, script blocked --
 * therefore never paints a control that cannot do anything, which is exactly
 * how the block this replaces went wrong: it called a `window.ShopifyInboxMessenger`
 * that does not exist and swallowed every click.
 */

const SELECTOR = '[data-chat-launcher]';

/** Reveal every launcher currently in the document. */
function reveal() {
  // Registered but never rendered is still nothing to launch.
  if (!document.querySelector('shopify-chat')) return;

  for (const button of document.querySelectorAll(SELECTOR)) {
    button.removeAttribute('hidden');
  }
}

// Delegated, so a section re-rendered by the theme editor keeps working without
// re-binding. The listener is harmless before the element upgrades: the buttons
// are hidden until then, and `show` is optional-called either way.
document.addEventListener('click', (event) => {
  const target = /** @type {Element | null} */ (event.target);
  if (!target?.closest?.(SELECTOR)) return;

  /** @type {{show?: () => void} | null} */
  const chat = document.querySelector('shopify-chat');
  chat?.show?.();
});

customElements.whenDefined('shopify-chat').then(() => {
  reveal();
  // The editor re-renders sections without re-running this module.
  document.addEventListener('shopify:section:load', reveal);
});
